import React from "react";
import TrafficLight from "../features/user-dashboard/components/TrafficLight";

/**
 * Unified Status Bubble Component
 *
 * Displays a status circle that matches PortStatus.jsx design.
 * Props:
 * - status: 'active' | 'inactive' | 'activating' | 'queued' | 'error'
 * - signal: (optional) Signal strength 0-31
 * - operator: (optional) Operator name
 * - animate: (optional) Pulse for activating
 * - showLabel: (optional) Show text label
 * - compact: (optional) Smaller version
 */
export default function StatusBubble({
  status = "inactive",
  signal = null,
  operator = null,
  animate = false,
  showLabel = true,
  compact = false,
}) {
  const getLabel = (s) => {
    const labels = {
      active: "Active",
      inactive: "Inactive",
      activating: "Activating",
      queued: "Queued",
      error: "Error",
    };
    return labels[s] || "Unknown";
  };

  const getBadgeClass = (s) => {
    const classes = {
      active: "bg-success",
      inactive: "bg-secondary",
      activating: "bg-warning text-dark",
      queued: "bg-warning text-dark",
      error: "bg-danger",
    };
    return classes[s] || "bg-secondary";
  };

  const getTooltip = () => {
    const parts = [];
    if (status === "activating") {
      parts.push("Activation in progress (20s warmup)");
    } else if (status === "active") {
      parts.push("Port is active");
      if (operator) parts.push(`Operator: ${operator}`);
      if (signal !== null) parts.push(`Signal: ${signal}/31`);
    } else if (status === "queued") {
      parts.push("Waiting in activation queue");
    } else if (status === "error") {
      parts.push("Activation failed");
    } else {
      parts.push("Port is inactive");
    }
    return parts.join("\n");
  };

  if (compact) {
    return (
      <TrafficLight
        status={status}
        animate={animate && status === "activating"}
      />
    );
  }

  return (
    <div className="d-flex align-items-center gap-2">
      <TrafficLight
        status={status}
        animate={animate && status === "activating"}
      />
      {showLabel && (
        <span
          className={`badge ${getBadgeClass(status)}`}
          title={getTooltip()}
          style={{
            fontSize: "0.85rem",
            padding: "0.4em 0.65em",
            cursor: "default",
          }}
        >
          {status === "activating" && (
            <span
              className="spinner-border spinner-border-sm me-2"
              style={{ width: "10px", height: "10px" }}
            />
          )}
          {getLabel(status)}
        </span>
      )}
      {signal !== null && status === "active" && (
        <span
          className="badge bg-light text-dark"
          title={`Signal strength: ${signal}/31`}
          style={{ fontSize: "0.75rem" }}
        >
          📶 {signal}
        </span>
      )}
    </div>
  );
}
